import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import mockData from '../../mockData/incidents.json';
import {
  HiOutlineClipboardDocumentCheck,
  HiOutlineExclamationTriangle,
  HiOutlineShieldCheck,
  HiOutlineCurrencyDollar,
  HiOutlineUserGroup,
  HiOutlineLightBulb,
  HiOutlineChevronRight,
  HiOutlineArrowLeft
} from 'react-icons/hi2';

import Step1_FIR from './steps/Step1_FIR';
import Step2_SafetyReview from './steps/Step2_SafetyReview';
import Step3_TeamFormation from './steps/Step3_TeamFormation';
import Step4_Investigation from './steps/Step4_Investigation';
import Step5_Recommendations from './steps/Step5_Recommendations';
import Step6_PlantHeadApproval from './steps/Step6_PlantHeadApproval';
import Step7_CostAnalysis from './steps/Step7_CostAnalysis';

const STEPS = [
  { id: 1, label: 'First Incident Report', icon: HiOutlineExclamationTriangle },
  { id: 2, label: 'Safety Office Review', icon: HiOutlineShieldCheck },
  { id: 3, label: 'Team Formation', icon: HiOutlineUserGroup },
  { id: 4, label: 'Detailed Investigation', icon: HiOutlineClipboardDocumentCheck },
  { id: 5, label: 'Recommendations', icon: HiOutlineLightBulb },
  { id: 6, label: 'Plant Head Approval', icon: HiOutlineShieldCheck },
  { id: 7, label: 'Cost Analysis', icon: HiOutlineCurrencyDollar }
];

const IncidentStepper = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [activeStep, setActiveStep] = useState(1);
  const [incidentData, setIncidentData] = useState(null);

  useEffect(() => {
    if (!isNew) {
      // Find mock data
      const incident = mockData.find(inc => inc.id === parseInt(id));
      if (incident) {
        setIncidentData(incident);
        setActiveStep(incident.currentStep);
      } else {
        // Fallback for not found
        console.error("Incident not found");
        navigate('/home/incident/dashboard');
      }
    } else {
      // Initialize empty incident for new reporting
      setIncidentData({
        id: Math.floor(Math.random() * 1000) + 10, // Mock id
        status: "Draft",
        currentStep: 1,
        incidentDate: new Date().toISOString().split('T')[0],
        type: "Injury",
        injuryDetails: {
          employeeType: "Company Employee"
        }
      });
    }
  }, [id, isNew, navigate]);

  const handleNextStep = (stepId) => {
    setActiveStep(stepId);
    setIncidentData(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep, stepId)
    }));
  };

  if (!incidentData) return <div className="p-8 text-center text-tertiary">Loading incident data...</div>;

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <>
            <Step1_FIR
              incidentData={incidentData}
              setIncidentData={setIncidentData}
              readOnly={incidentData.currentStep > 1}
            />
            {incidentData.currentStep <= 1 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => handleNextStep(2)}
                  className="px-6 py-2.5 text-white font-medium rounded-lg transition-colors shadow-sm"
                  style={{ backgroundColor: '#509183' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d7268'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#509183'}
                >
                  Submit FIR & Next
                </button>
              </div>
            )}
            {incidentData.currentStep > 1 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => setActiveStep(prev => prev + 1)}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  Next Step
                </button>
              </div>
            )}
          </>
        );
      case 2:
        return (
          <>
            <Step1_FIR
              incidentData={incidentData}
              readOnly={true}
            />
            <hr className="my-8 border-gray-200 border-2 border-dashed max-w-4xl mx-auto" />
            <Step2_SafetyReview
              incidentData={incidentData}
              readOnly={incidentData.currentStep > 2}
            />
            {incidentData.currentStep === 2 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => handleNextStep(3)}
                  className="px-6 py-2.5 text-white font-medium rounded-lg transition-colors shadow-sm"
                  style={{ backgroundColor: '#509183' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d7268'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#509183'}
                >
                  Approve FIR & Proceed
                </button>
              </div>
            )}
            {incidentData.currentStep > 2 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => setActiveStep(prev => prev + 1)}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  Next Step
                </button>
              </div>
            )}
          </>
        );
      case 3:
        return (
          <>
            <Step3_TeamFormation
              incidentData={incidentData}
              readOnly={incidentData.currentStep > 3}
            />
            {incidentData.currentStep === 3 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => handleNextStep(4)}
                  className="px-6 py-2.5 text-white font-medium rounded-lg transition-colors shadow-sm"
                  style={{ backgroundColor: '#509183' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d7268'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#509183'}
                >
                  Finalize Team & Next
                </button>
              </div>
            )}
            {incidentData.currentStep > 3 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => setActiveStep(prev => prev + 1)}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  Next Step
                </button>
              </div>
            )}
          </>
        );
      case 4:
        return (
          <>
            <Step4_Investigation
              incidentData={incidentData}
              readOnly={incidentData.currentStep > 4}
            />
            {incidentData.currentStep === 4 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto gap-4">
                <button
                  onClick={() => {/* Save draft logic */ }}
                  className="px-6 py-2.5 bg-card border border-gray-300 text-secondary hover:bg-page-bg font-medium rounded-lg transition-colors shadow-sm"
                >
                  Save Draft
                </button>
                <button
                  onClick={() => handleNextStep(5)}
                  className="px-6 py-2.5 text-white font-medium rounded-lg transition-colors shadow-sm"
                  style={{ backgroundColor: '#509183' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d7268'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#509183'}
                >
                  Submit RCA & Next
                </button>
              </div>
            )}
            {incidentData.currentStep > 4 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => setActiveStep(prev => prev + 1)}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  Next Step
                </button>
              </div>
            )}
          </>
        );
      case 5:
        return (
          <>
            <Step5_Recommendations
              incidentData={incidentData}
              readOnly={incidentData.currentStep > 5}
            />
            {incidentData.currentStep === 5 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto gap-4">
                <button
                  onClick={() => {/* Save draft logic */ }}
                  className="px-6 py-2.5 bg-card border border-gray-300 text-secondary hover:bg-page-bg font-medium rounded-lg transition-colors shadow-sm"
                >
                  Save Draft
                </button>
                <button
                  onClick={() => handleNextStep(6)}
                  className="px-6 py-2.5 text-white font-medium rounded-lg transition-colors shadow-sm"
                  style={{ backgroundColor: '#509183' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d7268'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#509183'}
                >
                  Submit for Approval
                </button>
              </div>
            )}
            {incidentData.currentStep > 5 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => setActiveStep(prev => prev + 1)}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  Next Step
                </button>
              </div>
            )}
          </>
        );
      case 6:
        return (
          <>
            <Step6_PlantHeadApproval
              incidentData={incidentData}
              readOnly={incidentData.currentStep > 6}
            />
            {incidentData.currentStep === 6 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto gap-4">
                <button
                  onClick={() => {/* Reject logic */ }}
                  className="px-6 py-2.5 bg-card border border-red-300 text-red-700 hover:bg-red-50 font-medium rounded-lg transition-colors shadow-sm"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleNextStep(7)}
                  className="px-6 py-2.5 text-white font-medium rounded-lg transition-colors shadow-sm"
                  style={{ backgroundColor: '#509183' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d7268'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#509183'}
                >
                  Approve & Proceed
                </button>
              </div>
            )}
            {incidentData.currentStep > 6 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => setActiveStep(prev => prev + 1)}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg transition-colors shadow-sm"
                >
                  Next Step
                </button>
              </div>
            )}
          </>
        );
      case 7:
        return (
          <>
            <Step7_CostAnalysis
              incidentData={incidentData}
              readOnly={incidentData.currentStep > 7}
            />
            {incidentData.currentStep <= 7 && (
              <div className="mt-6 flex justify-end px-6 max-w-4xl mx-auto">
                <button
                  onClick={() => {
                    alert('Incident Formally Closed');
                    navigate('/home/incident/dashboard');
                  }}
                  className="px-6 py-2.5 text-white font-medium rounded-lg transition-colors shadow-sm flex items-center gap-2"
                  style={{ backgroundColor: '#509183' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d7268'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#509183'}
                >
                  <HiOutlineCurrencyDollar className="w-5 h-5" />
                  Save Costs & Close
                </button>
              </div>
            )}
          </>
        );
      default:
        // Placeholder for the other steps
        return (
          <div className="bg-card p-8 rounded-xl border border-gray-200 shadow-sm min-h-[400px] flex items-center justify-center max-w-4xl mx-auto my-6">
            <div className="text-center max-w-md">
              <div className="mx-auto w-16 h-16 bg-blue-50 text-blue-500 flex items-center justify-center rounded-full mb-4">
                {React.createElement(STEPS[activeStep - 1].icon, { className: "w-8 h-8" })}
              </div>
              <h2 className="text-xl font-bold text-primary mb-2">Step {activeStep}: {STEPS[activeStep - 1].label}</h2>
              <p className="text-tertiary mb-6">Development for this specific step component is in progress.</p>

              <div className="flex gap-4 justify-center">
                {activeStep > 1 && (
                  <button
                    onClick={() => setActiveStep(prev => prev - 1)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-secondary font-medium rounded-lg transition-colors"
                  >
                    Previous Step
                  </button>
                )}
                {activeStep < 7 && (
                  <button
                    onClick={() => setActiveStep(prev => prev + 1)}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors shadow-sm"
                  >
                    Next Step
                  </button>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className="header-contain d-flex">
          <button
            onClick={() => navigate('/home/incident/dashboard')}
            className="oee-report-back-btn mr-2"
            title="Back to Dashboard"
          >
            <HiOutlineArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-primary tracking-tight">
                {isNew ? 'New Incident Report' : incidentData.incidentNumber}
              </h1>
              {!isNew && (
                <span className="px-2.5 py-0.5 rounded-md text-[14px] font-semibold   bg-blue-100 text-blue-800">
                  {incidentData.status}
                </span>
              )}
            </div>
            <p className="text-tertiary text-base">
              {isNew ? 'Follow the steps to submit a new First Incident Report (FIR)' : incidentData.title}
            </p>
          </div>
        </div>
      </div>
      <div className="page-content">
        {/* Header */}
        {/* <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/home/incident/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-lg text-tertiary transition-colors"
              title="Back to Dashboard"
            >
              <HiOutlineArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold text-primary tracking-tight">
                  {isNew ? 'New Incident Report' : incidentData.incidentNumber}
                </h1>
                {!isNew && (
                  <span className="px-2.5 py-0.5 rounded-md text-base font-bold uppercase tracking-wider bg-gray-200 text-primary">
                    {incidentData.status}
                  </span>
                )}
              </div>
              <p className="text-tertiary text-base">
                {isNew ? 'Follow the steps to submit a new First Incident Report (FIR)' : incidentData.title}
              </p>
            </div>
          </div>
        </div> */}

        <div className="flex flex-col lg:flex-row gap-2">
          {/* Left Sidebar - Stepper Navigation */}
          <div className="w-full lg:w-72 flex-shrink-0">
            <div className="bg-card rounded-xl border border-gray-200 shadow-sm overflow-hidden sticky top-0">
              <div className="p-4 bg-card border-b border-gray-200">
                <h3 className="font-semibold text-primary mb-0">Incident Progress</h3>
                <p className="px-2.5 py-0.5 rounded-md text-[14px] font-semibold   bg-green-100 text-green-800">Status: {incidentData.status}</p>
              </div>
              <div className="p-2">
                <nav className="flex flex-col space-y-1">
                  {STEPS.map((step, index) => {
                    const isActive = step.id === activeStep;
                    const isCompleted = step.id < incidentData.currentStep;
                    const isCurrentTarget = step.id === incidentData.currentStep;
                    const StepIcon = step.icon;

                    // Disable clicking ahead of the current progress
                    const isDisabled = step.id > incidentData.currentStep && !isCurrentTarget;

                    return (
                      <button
                        key={step.id}
                        onClick={() => !isDisabled && setActiveStep(step.id)}
                        disabled={isDisabled}
                        className={`
                        w-full flex items-center p-3 rounded-lg text-left transition-all duration-200
                        ${isActive ? 'bg-blue-50 border border-blue-100 shadow-sm' : 'border border-transparent hover:bg-gray-50'}
                        ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      >
                        <div className={`
                        flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3
                        ${isActive ? 'bg-blue-600 text-white' :
                            isCompleted ? 'bg-green-100 text-green-600' :
                              isCurrentTarget ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-400'}
                      `}>
                          {isCompleted && !isActive ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <span className="text-base font-semibold">{step.id}</span>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className={`text-base font-medium truncate ${isActive ? 'text-blue-900' : isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
                            {step.label}
                          </p>
                        </div>

                        {isActive && (
                          <HiOutlineChevronRight className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1">
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentStepper;
